import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

type InterviewState = 'disclaimer' | 'connecting' | 'active' | 'completed';

async function detectAmbientNoise(mediaStream: MediaStream, durationMs = 2000): Promise<number> {
  return new Promise((resolve) => {
    try {
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const source = audioContext.createMediaStreamSource(mediaStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let totalRms = 0;
      let samples = 0;

      const startTime = Date.now();
      const interval = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const normalized = (dataArray[i] - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        totalRms += rms;
        samples++;

        if (Date.now() - startTime >= durationMs) {
          clearInterval(interval);
          const avgRms = samples > 0 ? totalRms / samples : 0;
          try { audioContext.close(); } catch {}
          resolve(avgRms);
        }
      }, 100);
    } catch (err) {
      console.warn('Calibration failed, defaulting to quiet:', err);
      resolve(0);
    }
  });
}

function getVadConfig(noiseLevel: number) {
  if (noiseLevel < 0.015) {
    return { type: 'semantic_vad', eagerness: 'low', interrupt_response: false };
  } else if (noiseLevel < 0.04) {
    return { type: 'server_vad', threshold: 0.5, prefix_padding_ms: 400, silence_duration_ms: 1100, interrupt_response: false };
  } else {
    return { type: 'server_vad', threshold: 0.6, prefix_padding_ms: 500, silence_duration_ms: 1500, interrupt_response: false };
  }
}

function getCalibrationMessage(noiseLevel: number) {
  if (noiseLevel < 0.015) return '✓ Quiet space — preparing your interviewer...';
  if (noiseLevel < 0.04) return '✓ Moderate noise — adjusting for your space...';
  return '✓ Noisy environment — using extra-patient mode...';
}

export default function LiveInterview() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = (location.state as any)?.session;

  const [state, setState] = useState<InterviewState>('disclaimer');
  const [transcript, setTranscript] = useState<{ role: string; text: string }[]>([]);
  const [currentSpeaker, setCurrentSpeaker] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [scores, setScores] = useState<any>(null);
  const [statusText, setStatusText] = useState('Initializing...');

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);
  const transcriptRef = useRef('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);
  const vadConfigRef = useRef<any>(null);

  const sessionLengthMinutes = session?.sessionLengthMinutes || 15;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pcRef.current) pcRef.current.close();
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, currentSpeaker]);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => {
        if (prev >= sessionLengthMinutes * 60) {
          endSession();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const startInterview = async () => {
    setState('connecting');
    setStatusText('Connecting to your interviewer...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/realtime/session-token`,
        { sessionId: session.sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await connectWebRTC(response.data.ephemeralToken);
    } catch {
      setState('disclaimer');
      alert('Could not connect to interview session. Please try again.');
    }
  };

  const connectWebRTC = async (ephemeralToken: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    setStatusText('Calibrating audio environment...');
    let noiseLevel = 0;
    try {
      noiseLevel = await detectAmbientNoise(stream, 2000);
    } catch (err) {
      console.warn('Calibration error, falling back to quiet defaults', err);
    }
    const vadConfig = getVadConfig(noiseLevel);
    vadConfigRef.current = vadConfig;
    setStatusText(getCalibrationMessage(noiseLevel));

    const pc = new RTCPeerConnection();
    pcRef.current = pc;

    const audio = new Audio();
    audio.autoplay = true;
    audioRef.current = audio;

    pc.ontrack = (event) => { audio.srcObject = event.streams[0]; };
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;

    dc.onopen = () => {
      setState('active');
      setStatusText('Interviewer joining the room...');
      startTimer();

      dc.send(JSON.stringify({
        type: 'session.update',
        session: {
          type: 'realtime',
          audio: {
            output: { voice: 'alloy' },
            input: {
              transcription: { model: 'whisper-1' },
              turn_detection: vadConfigRef.current
            }
          }
        }
      }));
    };

    dc.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleEvent(data);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Proxy SDP exchange through our backend to avoid CORS issues
    const token = localStorage.getItem('token');
    const sdpResponse = await fetch(
      `${API_BASE_URL}/api/realtime/connect`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sdp: offer.sdp, ephemeralToken })
      }
    );
    const sdpData = await sdpResponse.json();
    if (!sdpData.answerSdp) throw new Error('No answer SDP from server');
    await pc.setRemoteDescription({ type: 'answer', sdp: sdpData.answerSdp });
  };

  const handleEvent = (event: any) => {
    if (event.type === 'session.updated' && !hasStartedRef.current && dcRef.current) {
      hasStartedRef.current = true;
      dcRef.current.send(JSON.stringify({
        type: 'response.create',
        response: {
          modalities: ['text', 'audio'],
          instructions: 'Begin the interview now. Introduce yourself as the interviewer in one sentence, then ask your first question.'
        }
      }));
      return;
    }

    switch (event.type) {
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        setCurrentSpeaker(prev => prev + (event.delta || ''));
        setStatusText('Interviewer speaking...');
        break;

      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        if (event.transcript) {
          setTranscript(prev => [...prev, { role: 'interviewer', text: event.transcript }]);
          transcriptRef.current += `\nInterviewer: ${event.transcript}`;
          setCurrentSpeaker('');
          setStatusText('Your turn — speak now');
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          setTranscript(prev => [...prev, { role: 'candidate', text: event.transcript }]);
          transcriptRef.current += `\nCandidate: ${event.transcript}`;
          setStatusText('Thinking...');
        }
        break;

      case 'response.created':
        setStatusText('Thinking...');
        break;

      case 'error':
        console.error('Realtime error:', event.error);
        break;
    }
  };

  const recalibrate = async () => {
    if (!streamRef.current || !dcRef.current || state !== 'active') return;
    const previousStatus = statusText;
    setStatusText('Recalibrating audio environment...');
    try {
      const noiseLevel = await detectAmbientNoise(streamRef.current, 2000);
      const newVadConfig = getVadConfig(noiseLevel);
      vadConfigRef.current = newVadConfig;
      dcRef.current.send(JSON.stringify({
        type: 'session.update',
        session: {
          type: 'realtime',
          audio: {
            output: { voice: 'alloy' },
            input: {
              transcription: { model: 'whisper-1' },
              turn_detection: newVadConfig
            }
          }
        }
      }));
      setStatusText(getCalibrationMessage(noiseLevel));
      setTimeout(() => setStatusText(previousStatus), 2500);
    } catch (err) {
      console.warn('Recalibration failed', err);
      setStatusText(previousStatus);
    }
  };

  const endSession = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pcRef.current) pcRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setState('completed');
    setStatusText('Generating your scorecard...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/realtime/complete`,
        { sessionId: session.sessionId, fullTranscript: transcriptRef.current, durationSeconds: elapsedSeconds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScores(response.data.scores);
    } catch {
      alert('Could not generate scorecard.');
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const remaining = (sessionLengthMinutes * 60) - elapsedSeconds;

  if (state === 'disclaimer') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.brand}>VIRILITER</p>
          <div style={styles.disclaimerCard}>
            <p style={styles.disclaimerIcon}>⚠️</p>
            <h2 style={styles.disclaimerTitle}>Before You Begin</h2>
            <p style={styles.disclaimerText}>
              This is an AI-powered role-play simulation. The interviewer is not affiliated with{' '}
              <span style={{ color: '#3B82F6', fontWeight: '600' }}>{session?.companyName || 'the company'}</span>{' '}
              or any real organization. This session is for practice purposes only.
            </p>
            <p style={styles.disclaimerSub}>
              You are about to enter a {sessionLengthMinutes}-minute live voice interview.
              Speak naturally — the AI will respond in real time.
              For best results, use a quiet space with headphones.
            </p>
          </div>
          <button style={styles.primaryButton} onClick={startInterview}>
            I Understand — Start Interview
          </button>
          <button style={styles.ghostButton} onClick={() => navigate('/setup')}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (state === 'connecting') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.connectingText}>{statusText}</p>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  if (state === 'completed') {
    if (!scores) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <p style={styles.brand}>VIRILITER</p>
            <p style={styles.connectingText}>Generating your scorecard...</p>
          </div>
        </div>
      );
    }
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, maxWidth: '600px', gap: '16px' }}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.scoreHeadline}>{scores.session_headline}</p>
          <p style={styles.overallScore}>
            {scores.overall_score}<span style={styles.scoreSuffix}>/100</span>
          </p>
          <div style={styles.readinessTag}>
            <span style={styles.readinessText}>
              {scores.readiness_assessment?.toUpperCase().replace('_', ' ')}
            </span>
          </div>
          <div style={styles.scoresGrid}>
            {[
              ['Presence', scores.score_executive_presence],
              ['Clarity', scores.score_strategic_clarity],
              ['Structure', scores.score_structured_thinking],
              ['Ownership', scores.score_risk_ownership],
            ].map(([label, value]) => (
              <div key={label} style={styles.scoreItem}>
                <p style={styles.scoreValue}>{value}</p>
                <p style={styles.scoreLabel}>{label}</p>
              </div>
            ))}
          </div>
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>TOP STRENGTH</p>
            <p style={styles.feedbackText}>{scores.top_strength}</p>
          </div>
          <div style={{ ...styles.feedbackBlock, borderLeftColor: '#EF4444' }}>
            <p style={{ ...styles.feedbackLabel, color: '#EF4444' }}>CRITICAL GAP</p>
            <p style={styles.feedbackText}>{scores.critical_gap}</p>
          </div>
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>COACHING FEEDBACK</p>
            <p style={styles.feedbackText}>{scores.overall_coaching_feedback}</p>
          </div>
          <div style={styles.retryButtons}>
            <button style={styles.retryPrimary} onClick={() => {
              if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
              if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
              if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
              transcriptRef.current = '';
              hasStartedRef.current = false;
              vadConfigRef.current = null;
              setTranscript([]);
              setCurrentSpeaker('');
              setElapsedSeconds(0);
              setScores(null);
              setStatusText('Initializing...');
              setState('disclaimer');
            }}>
              ↺ Try Again — Same Role
            </button>
            <button style={styles.retrySecondary} onClick={() => navigate('/setup', { state: { prefill: { background: (location.state as any)?.session?.candidateBackground } } })}>
              + New Role, Same Background
            </button>
            <button style={styles.retryGhost} onClick={() => navigate('/setup')}>Start Fresh</button>
            <button style={styles.retryGhost} onClick={() => navigate('/home')}>Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.interviewContainer}>
      <div style={styles.interviewHeader}>
        <p style={styles.brand}>VIRILITER</p>
        <div style={styles.timerBox}>
          <span style={{ ...styles.timer, color: remaining < 120 ? '#EF4444' : '#94A3B8' }}>
            {formatTime(remaining)}
          </span>
        </div>
      </div>

      <div style={styles.statusBar}>
        <div style={{
          ...styles.statusDot,
          backgroundColor: statusText.includes('speaking') ? '#22C55E' :
            statusText.includes('Thinking') ? '#F59E0B' :
            statusText.includes('Recalibrating') ? '#F59E0B' : '#3B82F6'
        }} />
        <span style={styles.statusText}>{statusText}</span>
        <button style={styles.recalibrateButton} onClick={recalibrate}>Recalibrate audio</button>
      </div>

      <div ref={scrollRef} style={styles.transcriptContainer}>
        {transcript.map((item, i) => (
          <div key={i} style={{
            ...styles.message,
            ...(item.role === 'interviewer' ? styles.messageInterviewer : styles.messageCandidate)
          }}>
            <p style={styles.messageRole}>{item.role === 'interviewer' ? 'INTERVIEWER' : 'YOU'}</p>
            <p style={styles.messageText}>{item.text}</p>
          </div>
        ))}
        {currentSpeaker && (
          <div style={{ ...styles.message, ...styles.messageInterviewer }}>
            <p style={styles.messageRole}>INTERVIEWER</p>
            <p style={styles.messageText}>{currentSpeaker}<span style={{ color: '#3B82F6' }}>|</span></p>
          </div>
        )}
      </div>

      <div style={styles.controlBar}>
        <div style={styles.micIndicator}>
          <div style={{
            ...styles.micDot,
            backgroundColor: statusText.includes('speak') && !statusText.includes('Interviewer') ? '#3B82F6' : '#1E293B'
          }} />
        </div>
        <span style={styles.micLabel}>{statusText}</span>
        <button style={styles.endButton} onClick={endSession}>End Session</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  card: { width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  disclaimerCard: { backgroundColor: '#0F172A', borderRadius: '16px', padding: '28px', border: '1px solid #1E293B', width: '100%', boxSizing: 'border-box' },
  disclaimerIcon: { fontSize: '32px', textAlign: 'center', margin: '0 0 12px 0' },
  disclaimerTitle: { color: '#F1F5F9', fontSize: '20px', fontWeight: '700', textAlign: 'center', margin: '0 0 16px 0' },
  disclaimerText: { color: '#94A3B8', fontSize: '14px', lineHeight: '1.7', textAlign: 'center', margin: '0 0 16px 0' },
  disclaimerSub: { color: '#475569', fontSize: '13px', textAlign: 'center', lineHeight: '1.6', margin: 0 },
  primaryButton: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '18px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  ghostButton: { backgroundColor: 'transparent', color: '#475569', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '12px' },
  connectingText: { color: '#64748B', fontSize: '16px', textAlign: 'center', margin: 0 },
  spinner: { width: '32px', height: '32px', border: '3px solid #1E293B', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  interviewContainer: { height: '100vh', backgroundColor: '#0A0F1E', display: 'flex', flexDirection: 'column' },
  interviewHeader: { padding: '20px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  timerBox: { backgroundColor: '#0F172A', borderRadius: '8px', padding: '6px 12px', border: '1px solid #1E293B' },
  timer: { fontSize: '14px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' },
  statusBar: { display: 'flex', alignItems: 'center', gap: '8px', padding: '0 24px 12px' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  statusText: { color: '#475569', fontSize: '12px', letterSpacing: '1px', flex: 1 },
  recalibrateButton: { background: 'transparent', color: '#475569', border: 'none', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 },
  transcriptContainer: { flex: 1, overflowY: 'auto', padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  message: { borderRadius: '12px', padding: '14px', maxWidth: '80%' },
  messageInterviewer: { backgroundColor: '#0F172A', alignSelf: 'flex-start', border: '1px solid #1E2D4A' },
  messageCandidate: { backgroundColor: '#1E3A5F', alignSelf: 'flex-end' },
  messageRole: { color: '#3B82F6', fontSize: '9px', letterSpacing: '2px', margin: '0 0 6px 0' },
  messageText: { color: '#E2E8F0', fontSize: '14px', lineHeight: '1.5', margin: 0 },
  controlBar: { padding: '16px 24px 32px', display: 'flex', alignItems: 'center', gap: '16px', borderTop: '1px solid #1E293B' },
  micIndicator: { width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#0F172A', border: '1px solid #1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  micDot: { width: '16px', height: '16px', borderRadius: '50%' },
  micLabel: { flex: 1, color: '#475569', fontSize: '13px' },
  endButton: { backgroundColor: '#1A0A0A', color: '#EF4444', border: '1px solid #EF4444', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  scoreHeadline: { color: '#94A3B8', fontSize: '14px', textAlign: 'center', margin: 0, lineHeight: '1.5' },
  overallScore: { color: '#F1F5F9', fontSize: '64px', fontWeight: '700', textAlign: 'center', margin: 0, letterSpacing: '-2px' },
  scoreSuffix: { fontSize: '20px', color: '#475569' },
  readinessTag: { backgroundColor: '#1E3A5F', borderRadius: '20px', padding: '6px 16px', border: '1px solid #3B82F6' },
  readinessText: { color: '#3B82F6', fontSize: '11px', letterSpacing: '2px', fontWeight: '600' },
  scoresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%' },
  scoreItem: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #1E293B' },
  scoreValue: { color: '#3B82F6', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  scoreLabel: { color: '#475569', fontSize: '10px', letterSpacing: '1px', margin: 0 },
  feedbackBlock: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', border: '1px solid #1E293B', borderLeft: '3px solid #3B82F6', width: '100%', boxSizing: 'border-box' },
  feedbackLabel: { color: '#3B82F6', fontSize: '10px', letterSpacing: '2px', fontWeight: '600', margin: '0 0 8px 0' },
  feedbackText: { color: '#94A3B8', fontSize: '14px', lineHeight: '1.6', margin: 0 },
  retryButtons: { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' },
  retryPrimary: { backgroundColor: '#3B82F6', color: '#fff', padding: '18px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  retrySecondary: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
  retryGhost: { backgroundColor: 'transparent', color: '#475569', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
};